/**
 * Activity Logger Utility
 * Use this helper to log activities from any component or store action
 */
import store from "@/store";
import { ActivityTypes } from "@/api/activityResource";

/**
 * Log an activity to the activity store
 * @param {Object} activityData - Activity data
 * @param {string} activityData.type - Activity type from ActivityTypes
 * @param {string} [activityData.description] - Optional custom description
 * @param {string} [activityData.entityType] - Type of entity (user, dashboard, data, role)
 * @param {number|string} [activityData.entityId] - ID of the entity
 * @param {string} [activityData.entityName] - Name of the entity
 * @param {Object} [activityData.details] - Additional details
 */
export function logActivity(activityData) {
  return store.dispatch("activity/logActivity", activityData);
}

/**
 * Log user login
 * @param {Object} userData - User data
 */
export function logUserLogin(userData) {
  return store.dispatch("activity/logLogin", userData);
}

/**
 * Log user logout
 */
export function logUserLogout() {
  return store.dispatch("activity/logLogout");
}

/**
 * Log dashboard action (create, update, delete, view)
 * @param {string} action - Action type: 'create', 'update', 'delete', 'view'
 * @param {Object} dashboard - Dashboard data
 */
export function logDashboardAction(action, dashboard) {
  return store.dispatch("activity/logDashboardAction", { action, dashboard });
}

/**
 * Log user management action
 * @param {string} action - Action type: 'create', 'update', 'delete', 'invite', 'roleChange'
 * @param {Object} targetUser - Target user data
 */
export function logUserAction(action, targetUser) {
  return store.dispatch("activity/logUserAction", { action, targetUser });
}

/**
 * Log data/file action
 * @param {string} action - Action type: 'upload', 'push', 'delete', 'update', 'fileUpload', 'fileDelete'
 * @param {Object} data - Data/file information
 */
export function logDataAction(action, data) {
  return store.dispatch("activity/logDataAction", { action, data });
}

/**
 * Log role action
 * @param {string} action - Action type: 'create', 'update', 'delete'
 * @param {Object} role - Role data
 */
export function logRoleAction(action, role) {
  return store.dispatch("activity/logRoleAction", { action, role });
}

// Export ActivityTypes for convenience
export { ActivityTypes };
